import { useState, useEffect, useMemo, useCallback } from 'react';

export const HIGH_VOLUME_COINS = new Set(['BTC', 'ETH']);
export const STORAGE_KEY = 'ct:thresholds';

export const DEFAULT_THRESHOLDS = Object.freeze({
  whaleHigh: 10_000_000,
  whaleAlt: 1_000_000,
  liqHigh: 1_000_000,
  liqAlt: 1_000_000,
});

export function getCoinTier(coin) {
  return HIGH_VOLUME_COINS.has((coin || '').toUpperCase()) ? 'high' : 'alt';
}

function getStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {}
  try {
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) return globalThis.localStorage;
  } catch {}
  return null;
}

export function loadThresholds() {
  const storage = getStorage();
  if (!storage) return { ...DEFAULT_THRESHOLDS };
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_THRESHOLDS };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_THRESHOLDS };
    return {
      whaleHigh: Number.isFinite(parsed.whaleHigh) ? parsed.whaleHigh : DEFAULT_THRESHOLDS.whaleHigh,
      whaleAlt: Number.isFinite(parsed.whaleAlt) ? parsed.whaleAlt : DEFAULT_THRESHOLDS.whaleAlt,
      liqHigh: Number.isFinite(parsed.liqHigh) ? parsed.liqHigh : DEFAULT_THRESHOLDS.liqHigh,
      liqAlt: Number.isFinite(parsed.liqAlt) ? parsed.liqAlt : DEFAULT_THRESHOLDS.liqAlt,
    };
  } catch {
    return { ...DEFAULT_THRESHOLDS };
  }
}

export function saveThresholds(state) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function resolveThresholds(state, coin) {
  const tier = getCoinTier(coin);
  return {
    whaleThreshold: tier === 'high' ? state.whaleHigh : state.whaleAlt,
    liqThreshold: tier === 'high' ? state.liqHigh : state.liqAlt,
  };
}

export function applyWhaleUpdate(state, coin, value) {
  const v = Number(value);
  const next = Number.isFinite(v) && v >= 0 ? v : 0;
  return getCoinTier(coin) === 'high'
    ? { ...state, whaleHigh: next }
    : { ...state, whaleAlt: next };
}

export function applyLiqUpdate(state, coin, value) {
  const v = Number(value);
  const next = Number.isFinite(v) && v >= 0 ? v : 0;
  return getCoinTier(coin) === 'high'
    ? { ...state, liqHigh: next }
    : { ...state, liqAlt: next };
}

export function useThresholds(coin) {
  const [thresholds, setThresholds] = useState(() => loadThresholds());

  useEffect(() => {
    saveThresholds(thresholds);
  }, [thresholds]);

  const { whaleThreshold, liqThreshold } = useMemo(
    () => resolveThresholds(thresholds, coin),
    [thresholds, coin]
  );

  const setWhaleThreshold = useCallback((value) => {
    setThresholds(prev => applyWhaleUpdate(prev, coin, value));
  }, [coin]);

  const setLiqThreshold = useCallback((value) => {
    setThresholds(prev => applyLiqUpdate(prev, coin, value));
  }, [coin]);

  return {
    thresholds,
    whaleThreshold,
    liqThreshold,
    setWhaleThreshold,
    setLiqThreshold,
  };
}
